import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@store/types';
import {
  DocumentCropSettings,
  ImageDocumentStoreItem,
  ODataDocumentCropSettings,
} from '../../../../components/v2/components/imageHandling/types/types';
import { updateImageDocumentInDict } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { convertAngle } from '@v2/components/imageHandling/helpers';
import { calculateCropDimensions } from '@reducers/v2/imageHandlingV2/thunks';

export interface RotateImageDocumentMessageHandlerPayload {
  documentId: number;
  rotationAngle: number;
  isCroppedVersion: boolean;
  pageNumber: number;
  cropSettings: ODataDocumentCropSettings | null;
}

export const rotateImageDocumentMessageHandler = createAsyncThunk<void, RotateImageDocumentMessageHandlerPayload>(
  'imageHandlingV2/rotateImageDocumentMessageHandler',
  ({ documentId, isCroppedVersion, pageNumber, rotationAngle, cropSettings }, { dispatch, getState }) => {
    const rootStore = getState() as RootState;
    const imageDocumentStore = rootStore.imageHandlingV2;

    // Get image directly from dictionary
    const image = imageDocumentStore.imagesDocumentsDict[documentId];

    if (!image) return;

    const initialCropAngle = image.cropSettings?.find((cs) => cs.pageNumber === image.pageNumber)?.rotationAngle;

    const newCustomRotate = isCroppedVersion && initialCropAngle != null ? convertAngle(rotationAngle - initialCropAngle) : rotationAngle;

    const updatedCropDimensions = cropSettings
      ? calculateCropDimensions({
          pageNumber: image.pageNumber,
          height: image.heightOriginal,
          width: image.widthOriginal,
          customAngle: convertAngle(cropSettings.rotationAngle ?? (initialCropAngle || 0) + newCustomRotate),
          cropSettings: [cropSettings],
        })
      : {};

    const updatedCropSettings = cropSettings
      ? image.cropSettings?.map((cs) => (cs.pageNumber === pageNumber ? { ...cropSettings, rotationAngle: cs.rotationAngle } : cs))
      : image.cropSettings;

    const updatedImageDocument: ImageDocumentStoreItem = {
      ...image,
      ...updatedCropDimensions,
      isProcessing: false,
      customRotate: { ...image.customRotate, [pageNumber]: newCustomRotate },
      ...(!isCroppedVersion
        ? {
            pageDetails: image.pageDetails.map((pd) => (pd.pageNumber === pageNumber ? { ...pd, rotationAngle } : pd)),
          }
        : {
            cropSettings: updatedCropSettings,
          }),
    } as ImageDocumentStoreItem;

    dispatch(updateImageDocumentInDict({ documentId, updatedImageDocument }));
  }
);
